import { TASK_PROGRESS_STEPS, type Role, type TaskProgress } from './constants';
import {
  addDays,
  belgradeToUtc,
  dateOf,
  dateTime,
  daysInMonth,
  fmtDate,
  isoToday,
  type HHMM,
  type IsoDate,
} from './dates';

/**
 * Tasks (Detyrat e punëtorëve) — pure ports of the prototype's task helpers (lines 2295-2332). Every label and
 * toast text is copied verbatim into TASK_TEXT. Day-level logic runs in Europe/Belgrade (task.md §6.1).
 */

export const TASK_STATUSES = ['pending', 'progress', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_PRIORITIES = ['normal', 'high'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const TASK_REPEATS = ['none', 'daily', 'weekly', 'monthly'] as const;
export type TaskRepeat = (typeof TASK_REPEATS)[number];
export const TASK_TABS = ['today', 'pending', 'progress', 'done'] as const;
export type TaskTab = (typeof TASK_TABS)[number];

/** Prototype #taskDueTime default value. */
export const TASK_DEFAULT_DUE_TIME: HHMM = '17:00';
/** Prototype taskDateTimeValue: a due date without a time is overdue after 23:59. */
export const TASK_OVERDUE_DEFAULT_TIME: HHMM = '23:59';
/** Prototype taskProgressHistoryHtml shows the last 3 history entries. */
export const TASK_PROGRESS_HISTORY_DISPLAY = 3;
/** Prototype renderTaskDashboardPreview lists at most 6 tasks. */
export const TASK_DASHBOARD_PREVIEW_LIMIT = 6;
/** Prototype finishAuthLogin: setTimeout(showTaskLoginNotice, 180). */
export const TASK_LOGIN_NOTICE_DELAY_MS = 180;

/** Step button labels (prototype taskProgressHtml) — 75 deliberately differs from taskProgressLabel(75). */
export const TASK_STEP_LABELS: Readonly<Record<TaskProgress, string>> = {
  0: 'Caktuar',
  25: 'Filluar',
  50: 'Në proces',
  75: 'Afër fundit',
  100: 'Kryer',
};

export const TASK_TEXT = {
  status: { pending: 'Në pritje', progress: 'Në punë', done: 'Kryer' },
  progress: {
    assigned: 'Caktuar',
    started: 'Filluar',
    inProcess: 'Në proces',
    nearEnd: 'Afër përfundimit',
    done: 'Kryer',
  },
  repeat: { none: 'Vetëm një herë', daily: 'Çdo ditë', weekly: 'Çdo javë', monthly: 'Çdo muaj' },
  priority: { high: 'Prioritet i lartë', normal: 'Normal' },
  tabs: { today: 'Sot', pending: 'Në pritje', progress: 'Në punë', done: 'Të kryera' },
  unknownEmployee: 'Punëtor i panjohur',
  defaultInitials: 'P',
  noDue: 'Pa afat',
  dueTimeSeparator: ' · ',
  overdueChipPrefix: '⚠ Afati kaluar · ',
  repeatChipPrefix: '↻ ',
  fallbackTitle: 'Detyrë',
  person: { done: 'Kryer ', started: 'Filluar ', assigned: 'Detyrë e caktuar' },
  progressBox: {
    title: 'Progresi i detyrës · ',
    updated: 'Përditësuar: ',
    history: 'Procesi:',
    historyJoin: ' → ',
  },
  actions: { reopen: 'Rihap', edit: 'Edito', delete: 'Fshi' },
  kpis: { active: 'Aktive', today: 'Për sot', overdue: 'Të vonuara', doneMonth: 'Kryera këtë muaj' },
  subtitle: {
    admin: 'Cakto detyra dhe përcill qartë progresin 0–100% për secilin punëtor.',
    staff: (name: string): string => 'Po i sheh detyrat dhe progresin e ' + name + '.',
    staffNoIdentity: 'Zgjedh emrin te “Kush po punon?” për me i pa detyrat e tua.',
  },
  staffPrompt: {
    title: 'Zgjedh emrin tënd',
    text: 'Pastaj ERP-ja t’i shfaq vetëm detyrat që të janë caktuar ty dhe progresin e secilës.',
  },
  emptyList: 'Nuk ka detyra në këtë kategori.',
  teamEmpty: 'Nuk ka punëtorë të regjistruar.',
  assigneeCount: (n: number): string => (n === 1 ? '1 i zgjedhur' : n + ' të zgjedhur'),
  toasts: {
    onlyAdminOpen: 'Vetëm Drejtori / Administratori mund të caktojë detyra',
    onlyAdminSave: 'Vetëm Administratori mund të caktojë detyra',
    noEmployees: 'Së pari regjistro punëtorët te Pjesëmarrja',
    titleRequired: 'Shkruaj detyrën',
    assigneeRequired: 'Zgjedh të paktën një punëtor',
    notYours: 'Kjo detyrë nuk është e punëtorit të zgjedhur',
    alreadyAt: (p: number): string => 'Progresi është tashmë ' + p + '%',
    progressSet: (p: number): string => 'Progresi u vendos në ' + p + '% · ' + taskProgressLabel(p),
    deleted: 'Detyra u fshi',
    confirmDelete: (title: string): string => 'A dëshiron me e fshi detyrën “' + title + '”?',
    assignedTo: (name: string): string => 'Detyra u caktua për ' + name,
    assignedToMany: (n: number): string => 'Detyra u caktua për ' + n + ' punëtorë',
    updated: 'Detyra u përditësua',
    updatedAdded: (n: number): string =>
      'Detyra u përditësua dhe iu shtua edhe ' + n + ' punëtor' + (n === 1 ? '' : 'ë'),
    staffActive: (name: string): string => 'Detyrat e ' + name + ' janë aktive',
  },
  log: {
    assigned: 'U caktua detyrë',
    updated: 'U përditësua detyra',
    progress: 'U përditësua progresi',
    spawned: 'U krijua automatikisht detyra e radhës',
    deleted: 'U fshi detyra',
  },
  loginNotice: {
    overdue: (n: number): string => 'Ka ' + n + ' detyrë/a të vonuara',
    today: (n: number): string => 'Ka ' + n + ' detyrë/a për sot',
  },
  dashboard: {
    title: 'Detyrat e sotme',
    defaultSubtitle: 'Punët që duhen kryer sot ose që kanë kaluar afatin',
    subtitleAdmin: 'Punët e sotme me progresin aktual për krejt ekipin',
    subtitleStaff: (name: string): string => 'Detyrat e ' + name + ' për sot',
    subtitleNoIdentity: 'Zgjedh punëtorin për me i pa detyrat personale',
    promptTitle: 'Kush po punon?',
    promptText:
      'Hape Detyrat dhe zgjedh emrin e punëtorit. Pastaj këtu dalin automatikisht punët e tij për sot.',
    open: 'Hap Detyrat',
    empty: 'Nuk ka detyra për sot.',
    overduePrefix: 'VONUAR · ',
  },
} as const;

export interface TaskHistoryEntry {
  progress: number;
  /** ISO instant of the change. */
  at: string;
}

/** Task shape the pure helpers work on (prototype db.tasks entry; the web maps DB rows to it). */
export interface TaskLike {
  id: string;
  groupId: string;
  title: string;
  description: string;
  /** yyyy-MM-dd, '' when the task has no due date. */
  due: IsoDate;
  /** HH:mm, '' when the task has no due time. */
  dueTime: HHMM;
  priority: TaskPriority;
  repeat: TaskRepeat;
  assigneeId: string | null;
  status: TaskStatus;
  progress: number | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  previousTaskId?: string | null;
  history?: readonly TaskHistoryEntry[];
}

export interface TaskEmployeeLike {
  id: string;
  name: string;
}

export function isTaskStatus(v: unknown): v is TaskStatus {
  return typeof v === 'string' && (TASK_STATUSES as readonly string[]).includes(v);
}
export function isTaskPriority(v: unknown): v is TaskPriority {
  return typeof v === 'string' && (TASK_PRIORITIES as readonly string[]).includes(v);
}
export function isTaskRepeat(v: unknown): v is TaskRepeat {
  return typeof v === 'string' && (TASK_REPEATS as readonly string[]).includes(v);
}
export function isTaskTab(v: unknown): v is TaskTab {
  return typeof v === 'string' && (TASK_TABS as readonly string[]).includes(v);
}
export function isTaskProgress(v: unknown): v is TaskProgress {
  return typeof v === 'number' && (TASK_PROGRESS_STEPS as readonly number[]).includes(v);
}

/** Prototype taskStatusLabel (dead code in the prototype, ported for parity). */
export function taskStatusLabel(v: string | null | undefined): string {
  return v === 'done'
    ? TASK_TEXT.status.done
    : v === 'progress'
      ? TASK_TEXT.status.progress
      : TASK_TEXT.status.pending;
}

/** Prototype taskProgress: numeric progress clamped to 0–100, else derived from the status. */
export function taskProgress(t: Pick<TaskLike, 'progress' | 'status'> | null | undefined): number {
  const p = t?.progress == null ? Number.NaN : Number(t.progress);
  if (Number.isFinite(p)) return Math.max(0, Math.min(100, p));
  return t?.status === 'done' ? 100 : t?.status === 'progress' ? 50 : 0;
}

/** Prototype taskProgressLabel. */
export function taskProgressLabel(p: number | null | undefined): string {
  const v = Number(p || 0);
  if (v >= 100) return TASK_TEXT.progress.done;
  if (v >= 75) return TASK_TEXT.progress.nearEnd;
  if (v >= 50) return TASK_TEXT.progress.inProcess;
  if (v >= 25) return TASK_TEXT.progress.started;
  return TASK_TEXT.progress.assigned;
}

/** Prototype taskProgressUpdatedAt: last history entry, else completedAt / startedAt / updatedAt / createdAt. */
export function taskProgressUpdatedAt(t: TaskLike | null | undefined): string {
  const h = t?.history ?? [];
  const last = h[h.length - 1];
  return last?.at || t?.completedAt || t?.startedAt || t?.updatedAt || t?.createdAt || '';
}

/** Prototype taskProgressHistoryHtml entries: `Filluar 25% · 05/09/2026 08:00` for the last 3 changes. */
export function taskHistoryTrail(t: TaskLike | null | undefined): string[] {
  const h = t?.history ?? [];
  return h
    .slice(-TASK_PROGRESS_HISTORY_DISPLAY)
    .map((x) => `${taskProgressLabel(x.progress)} ${x.progress}% · ${dateTime(x.at)}`);
}

/** Prototype taskRepeatLabel. */
export function taskRepeatLabel(v: string | null | undefined): string {
  return v === 'daily'
    ? TASK_TEXT.repeat.daily
    : v === 'weekly'
      ? TASK_TEXT.repeat.weekly
      : v === 'monthly'
        ? TASK_TEXT.repeat.monthly
        : TASK_TEXT.repeat.none;
}

/** Prototype taskEmployees: copy sorted by name with the 'sq' collation. */
export function taskEmployees<T extends TaskEmployeeLike>(list: readonly T[] | null | undefined): T[] {
  return [...(list ?? [])].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sq'));
}

/** Prototype taskEmployee. */
export function taskEmployee<T extends TaskEmployeeLike>(
  list: readonly T[],
  id: string | null | undefined,
): T | null {
  return list.find((x) => x.id === id) ?? null;
}

/** Prototype taskUserName: employee name or 'Punëtor i panjohur'. */
export function taskUserName(employees: readonly TaskEmployeeLike[], id: string | null | undefined): string {
  return taskEmployee(employees, id)?.name ?? TASK_TEXT.unknownEmployee;
}

/** Prototype taskEmployeeInitials: first letters of the first two words, upper-cased, 'P' when empty. */
export function taskEmployeeInitials(name: string | null | undefined): string {
  return (
    String(name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0] ?? '')
      .join('')
      .toUpperCase() || TASK_TEXT.defaultInitials
  );
}

/** Prototype taskDateTimeValue: due date + time (default 23:59) as an instant in Europe/Belgrade; null without a due. */
export function taskDateTimeValue(t: Pick<TaskLike, 'due' | 'dueTime'> | null | undefined): Date | null {
  if (!t?.due) return null;
  const time = (t.dueTime || TASK_OVERDUE_DEFAULT_TIME).slice(0, 5);
  const d = belgradeToUtc(t.due, time);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Prototype taskIsOverdue. */
export function taskIsOverdue(t: TaskLike | null | undefined, now: Date = new Date()): boolean {
  if (!t || t.status === 'done' || !t.due) return false;
  const d = taskDateTimeValue(t);
  return d ? d.getTime() < now.getTime() : t.due < isoToday(now);
}

/** Prototype taskDueLabel: 'Pa afat' | '05/09/2026' | '05/09/2026 · 17:00'. */
export function taskDueLabel(t: Pick<TaskLike, 'due' | 'dueTime'> | null | undefined): string {
  if (!t?.due) return TASK_TEXT.noDue;
  let txt = fmtDate(t.due);
  if (t.dueTime) txt += TASK_TEXT.dueTimeSeparator + t.dueTime.slice(0, 5);
  return txt;
}

/** Prototype renderTasks person line: 'Kryer <dt>' | 'Filluar <dt>' | 'Detyrë e caktuar'. */
export function taskPersonLine(t: TaskLike): string {
  if (t.status === 'done' && t.completedAt) return TASK_TEXT.person.done + dateTime(t.completedAt);
  if (t.startedAt) return TASK_TEXT.person.started + dateTime(t.startedAt);
  return TASK_TEXT.person.assigned;
}

/**
 * Prototype nextRecurringTaskDate: daily +1, weekly +7, monthly = same day next month clamped to that month's
 * last day (31.01 → 28.02 / 29.02); '' for a non-repeating task. `today` replaces an empty due date.
 */
export function nextRecurringTaskDate(
  due: IsoDate | null | undefined,
  repeat: string | null | undefined,
  today: IsoDate = isoToday(),
): IsoDate {
  const base = due || today;
  if (repeat === 'daily') return addDays(base, 1);
  if (repeat === 'weekly') return addDays(base, 7);
  if (repeat === 'monthly') {
    const parts = base.split('-').map(Number);
    const y = parts[0] || 1970;
    const m = parts[1] || 1;
    const day = parts[2] || 1;
    const nextMonth = m === 12 ? 1 : m + 1;
    const nextYear = m === 12 ? y + 1 : y;
    const first = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
    const last = daysInMonth(first);
    return `${first.slice(0, 8)}${String(Math.min(day, last)).padStart(2, '0')}`;
  }
  return '';
}

/** Prototype setTaskStatus: the progress a status transition maps to (pending 0, done 100, progress keeps 1–99 else 25). */
export function taskStatusProgress(
  t: Pick<TaskLike, 'progress' | 'status'>,
  status: TaskStatus,
): TaskProgress {
  if (status === 'pending') return 0;
  if (status === 'done') return 100;
  const p = taskProgress(t);
  return isTaskProgress(p) && p > 0 && p < 100 ? p : 25;
}

/** Prototype taskMatchesCurrentTab. */
export function taskMatchesTab(t: TaskLike, tab: TaskTab, today: IsoDate = isoToday()): boolean {
  if (tab === 'done') return t.status === 'done';
  if (tab === 'progress') return t.status === 'progress';
  if (tab === 'pending') return t.status === 'pending';
  return t.status !== 'done' && !!t.due && t.due <= today;
}

/**
 * Prototype visibleTasks, widened at the shop's request: the director and every staff login see all the tasks
 * (the prototype showed staff only their own employee's). A delivery login sees none.
 */
export function visibleTasks<T extends TaskLike>(tasks: readonly T[], role: Role | null | undefined): T[] {
  return role === 'admin' || role === 'staff' ? [...tasks] : [];
}

export interface TaskListFilter {
  tab: TaskTab;
  /** Raw search text (prototype #taskSearch). */
  query: string;
  /** 'all' (admin default), an employee id, or '' (no restriction). */
  who: string;
  employees: readonly TaskEmployeeLike[];
  today?: IsoDate;
  now?: Date;
}

/** Prototype taskFilteredList sort: open before done, overdue first, then due date, then due time. */
export function compareTasks(a: TaskLike, b: TaskLike, now: Date = new Date()): number {
  return (
    Number(a.status === 'done') - Number(b.status === 'done') ||
    Number(taskIsOverdue(b, now)) - Number(taskIsOverdue(a, now)) ||
    String(a.due || '9999').localeCompare(String(b.due || '9999')) ||
    String(a.dueTime || '99:99').localeCompare(String(b.dueTime || '99:99'))
  );
}

/** Prototype taskFilteredList over an already role-filtered list. */
export function filterAndSortTasks<T extends TaskLike>(tasks: readonly T[], filter: TaskListFilter): T[] {
  const now = filter.now ?? new Date();
  const today = filter.today ?? isoToday(now);
  const q = filter.query.trim().toLocaleLowerCase('sq-AL');
  const who = filter.who;
  return tasks
    .filter(
      (t) =>
        taskMatchesTab(t, filter.tab, today) &&
        (who === 'all' || !who || t.assigneeId === who) &&
        (!q ||
          `${t.title || ''} ${t.description || ''} ${taskUserName(filter.employees, t.assigneeId)}`
            .toLocaleLowerCase('sq-AL')
            .includes(q)),
    )
    .sort((a, b) => compareTasks(a, b, now));
}

export interface TaskKpis {
  open: number;
  today: number;
  overdue: number;
  doneMonth: number;
}

/** Month key (yyyy-MM, Europe/Belgrade) of a completion instant; '' when missing. */
export function completedMonth(completedAt: string | null | undefined): string {
  if (!completedAt) return '';
  const d = new Date(completedAt);
  return Number.isNaN(d.getTime()) ? '' : dateOf(d).slice(0, 7);
}

/** Prototype renderTasks KPI strip (Aktive / Për sot / Të vonuara / Kryera këtë muaj) over the visible tasks. */
export function taskKpis(
  tasks: readonly TaskLike[],
  today: IsoDate = isoToday(),
  now: Date = new Date(),
): TaskKpis {
  const open = tasks.filter((t) => t.status !== 'done');
  return {
    open: open.length,
    today: open.filter((t) => t.due === today).length,
    overdue: open.filter((t) => taskIsOverdue(t, now)).length,
    doneMonth: tasks.filter((t) => t.status === 'done' && completedMonth(t.completedAt) === today.slice(0, 7))
      .length,
  };
}

/** Prototype updateTaskNavBadge: open visible tasks. */
export function openTaskCount(tasks: readonly TaskLike[]): number {
  return tasks.filter((t) => t.status !== 'done').length;
}

export interface TeamSummaryRow {
  employeeId: string;
  name: string;
  active: number;
  today: number;
  avgProgress: number;
  overdue: number;
  doneMonth: number;
}

/** Prototype renderTaskTeamSummary numbers per employee (sorted like taskEmployees) over ALL tasks. */
export function teamSummary(
  tasks: readonly TaskLike[],
  employees: readonly TaskEmployeeLike[],
  today: IsoDate = isoToday(),
  now: Date = new Date(),
): TeamSummaryRow[] {
  const month = today.slice(0, 7);
  return taskEmployees(employees).map((e) => {
    const list = tasks.filter((t) => t.assigneeId === e.id);
    const active = list.filter((t) => t.status !== 'done');
    return {
      employeeId: e.id,
      name: e.name,
      active: active.length,
      today: active.filter((t) => t.due === today).length,
      avgProgress: active.length
        ? Math.round(active.reduce((a, t) => a + taskProgress(t), 0) / active.length)
        : 0,
      overdue: active.filter((t) => taskIsOverdue(t, now)).length,
      doneMonth: list.filter((t) => t.status === 'done' && completedMonth(t.completedAt) === month).length,
    };
  });
}

export interface DashboardTaskRow<T extends TaskLike = TaskLike> {
  task: T;
  overdue: boolean;
  progress: number;
  progressLabel: string;
  /** Prototype .task-dash-date text: 'VONUAR · ' prefix, then due time or the formatted due date. */
  dateText: string;
}

/** Prototype renderTaskDashboardPreview list: open tasks due today or earlier, overdue first, at most 6. */
export function dashboardTaskPreview<T extends TaskLike>(
  tasks: readonly T[],
  today: IsoDate = isoToday(),
  now: Date = new Date(),
): DashboardTaskRow<T>[] {
  return tasks
    .filter((t) => t.status !== 'done' && !!t.due && t.due <= today)
    .sort(
      (a, b) =>
        Number(taskIsOverdue(b, now)) - Number(taskIsOverdue(a, now)) ||
        String(a.due || '').localeCompare(String(b.due || '')) ||
        String(a.dueTime || '').localeCompare(String(b.dueTime || '')),
    )
    .slice(0, TASK_DASHBOARD_PREVIEW_LIMIT)
    .map((task) => {
      const overdue = taskIsOverdue(task, now);
      const progress = taskProgress(task);
      return {
        task,
        overdue,
        progress,
        progressLabel: taskProgressLabel(progress),
        dateText: (overdue ? TASK_TEXT.dashboard.overduePrefix : '') + (task.dueTime || fmtDate(task.due)),
      };
    });
}

/** Prototype showTaskLoginNotice text over the visible tasks; null when nothing is due. */
export function loginNotice(
  tasks: readonly TaskLike[],
  today: IsoDate = isoToday(),
  now: Date = new Date(),
): string | null {
  const open = tasks.filter((t) => t.status !== 'done');
  const over = open.filter((t) => taskIsOverdue(t, now));
  if (over.length) return TASK_TEXT.loginNotice.overdue(over.length);
  const due = open.filter((t) => t.due === today);
  if (due.length) return TASK_TEXT.loginNotice.today(due.length);
  return null;
}

/** Prototype taskMatchesCurrentTab inverted: the tab a task shows up in (deep links /tasks/:id). */
export function tabForTask(t: TaskLike, today: IsoDate = isoToday()): TaskTab {
  if (t.status === 'done') return 'done';
  if (t.status === 'progress') return 'progress';
  return taskMatchesTab(t, 'today', today) ? 'today' : 'pending';
}
